import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'
import { LESSON_INDEX } from '../content/lessonIndex'

export function useProgress() {
  const { user } = useAuth()
  const [progress, setProgress] = useState({
    completedLessons: [],
    quizScores: {},
    capstonePassed: false,
    certificateUnlocked: false,
    credentialId: null,
    credentialIssuedAt: null,
  })
  const [loading, setLoading] = useState(true)

  const fetchProgress = useCallback(async () => {
    if (!user) return

    try {
      const { data: progressRow, error: progressErr } = await supabase
        .from('progress')
        .select('*')
        .eq('user_id', user.id)
        .single()

      // Own-row credential — RLS policy "credentials: select own row"
      // allows authenticated user to read only their own row.
      const { data: credRow } = await supabase
        .from('credentials')
        .select('id, issued_at')
        .eq('user_id', user.id)
        .eq('kind', 'foundation-ready')
        .maybeSingle()

      if (!progressErr && progressRow) {
        setProgress({
          completedLessons:     progressRow.completed_lessons || [],
          quizScores:           progressRow.quiz_scores || {},
          capstonePassed:       progressRow.capstone_passed || false,
          certificateUnlocked:  progressRow.certificate_unlocked || false,
          credentialId:         credRow?.id || null,
          credentialIssuedAt:   credRow?.issued_at || null,
        })
      }
    } catch (err) {
      console.error('useProgress fetch failed:', err)
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    if (!user) {
      setLoading(false)
      return
    }
    fetchProgress()
  }, [user, fetchProgress])

  const markLessonComplete = useCallback(async (lessonId, quizScore) => {
    if (!user) return { error: new Error('Not authenticated') }

    const { data, error: rpcErr } = await supabase
      .rpc('mark_lesson_complete', {
        p_lesson_id:  lessonId,
        p_quiz_score: quizScore ?? null,
      })

    if (rpcErr) {
      return { error: rpcErr }
    }

    if (data && data.success === false) {
      const err = new Error(data.error || 'Could not mark lesson complete')
      err.code = data.code || null
      return { error: err }
    }

    setProgress(prev => ({
      ...prev,
      completedLessons: [...new Set([...prev.completedLessons, lessonId])],
      quizScores:       quizScore != null
        ? { ...prev.quizScores, [lessonId]: quizScore }
        : prev.quizScores,
    }))

    return { error: null }
  }, [user])

  const completeCapstone = useCallback(async () => {
    if (!user) return { error: new Error('Not authenticated'), credentialId: null }

    // Step 1: pass_foundation_capstone RPC
    const { data: passData, error: passErr } = await supabase
      .rpc('pass_foundation_capstone')

    if (passErr) return { error: passErr, credentialId: null }

    if (passData && passData.success === false) {
      const err = new Error(passData.error || 'Could not record capstone completion')
      err.code = passData.code || null
      return { error: err, credentialId: null }
    }

    // Step 2: issue_foundation_credential RPC
    const { data: credentialId, error: rpcErr } = await supabase
      .rpc('issue_foundation_credential')

    if (rpcErr) return { error: rpcErr, credentialId: null }

    await fetchProgress()

    return { error: null, credentialId }
  }, [user, fetchProgress])

  const markCapstonePassed = completeCapstone

  const isLessonComplete = useCallback((lessonId) => {
    return progress.completedLessons.includes(lessonId)
  }, [progress.completedLessons])

  // Derive totals from LESSON_INDEX rather than hardcoding — adding or
  // removing a lesson updates the stats automatically. Exclude intros,
  // refs, and the capstone:
  //   - Intros don't have a mark-complete action
  //   - Refs are reference pages, also no mark-complete
  //   - Capstone is tracked separately via capstone_passed
  const totals = useMemo(() => {
    const isCountable = l =>
      l.type !== 'intro' && l.type !== 'capstone' && l.type !== 'ref'
    return {
      itFoundation:  LESSON_INDEX.filter(l => l.track === 'it' && isCountable(l)).length,
      humanSkills:   LESSON_INDEX.filter(l => l.track === 'hs' && isCountable(l)).length,
      aiFoundations: LESSON_INDEX.filter(l => l.track === 'ai' && isCountable(l)).length,
    }
  }, [])

  const isItLesson = id => /^\d/.test(id)
  const rawItCompleted = progress.completedLessons.filter(isItLesson).length
  const rawHsCompleted = progress.completedLessons.filter(id => id.startsWith('hs-')).length
  const rawAiCompleted = progress.completedLessons.filter(id => id.startsWith('ai-')).length

  const itCompleted = Math.min(rawItCompleted, totals.itFoundation)
  const hsCompleted = Math.min(rawHsCompleted, totals.humanSkills)
  const aiCompleted = Math.min(rawAiCompleted, totals.aiFoundations)

  const overallTotal = totals.itFoundation + totals.humanSkills + totals.aiFoundations

  return {
    progress,
    loading,
    markLessonComplete,
    completeCapstone,
    markCapstonePassed,
    isLessonComplete,
    stats: {
      itFoundation:  { completed: itCompleted,  total: totals.itFoundation },
      humanSkills:   { completed: hsCompleted,  total: totals.humanSkills },
      aiFoundations: { completed: aiCompleted,  total: totals.aiFoundations },
      overallPercent: overallTotal > 0
        ? Math.round(((itCompleted + hsCompleted + aiCompleted) / overallTotal) * 100)
        : 0,
    },
    refreshProgress: fetchProgress,
  }
}
